import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Bot, Send, Loader2, Shield, User, RefreshCw } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;

const starterCards = [
  {
    label: 'QUICK CHECK',
    question: 'How are my English campaigns performing this month?'
  },
  {
    label: 'PRIORITY',
    question: 'Which campaign deserves the most attention right now?'
  },
  {
    label: 'BUDGET',
    question: 'Where can I move budget without losing conversions?'
  },
  {
    label: 'SEARCH TERMS',
    question: 'Are there search terms wasting budget?'
  }
];

const slashShortcuts = [
  {
    command: '/campaign',
    prompt: 'Show campaign-level performance and tell me which campaigns need attention.'
  },
  {
    command: '/searchterm',
    prompt: 'Show the search terms that are wasting the most budget and explain what to review.'
  },
  {
    command: '/compare',
    prompt: 'Compare this period with the previous period and explain what changed.'
  }
];

export default function AccountChat({ selectedAccount }) {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const accountId = selectedAccount?.id || selectedAccount?.customerId;
  const accountName = selectedAccount?.name || selectedAccount?.accountName || accountId || 'No account selected';
  const hasConversation = messages.length > 0;

  const sendMessage = async (question = input) => {
    const rawText = String(question || '').trim();
    const shortcut = slashShortcuts.find(item => item.command === rawText.toLowerCase());
    const text = shortcut?.prompt || rawText;
    if (!text || loading) return;
    if (!accountId) {
      setError('Select a Google Ads account first.');
      return;
    }

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setLoading(true);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/account-chat/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Account-Id': accountId
        },
        body: JSON.stringify({
          message: text,
          history: nextMessages.slice(-8),
          days
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: data.answer, meta: data.context }]);
    } catch (err) {
      setError(err.message || 'Could not analyze this account.');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I could not analyze the account yet. Please check the selected account and try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: {
      minHeight: '100%',
      padding: 24,
      color: theme.colors.text.primary,
      background: theme.colors.bg.primary
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 16,
      alignItems: 'center',
      marginBottom: 14
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      margin: 0,
      fontSize: 18,
      fontWeight: 800
    },
    avatar: {
      width: 36,
      height: 36,
      borderRadius: 12,
      display: 'grid',
      placeItems: 'center',
      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
      color: 'white',
      fontWeight: 900,
      boxShadow: '0 14px 30px rgba(99, 102, 241, 0.28)'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 10px',
      borderRadius: 999,
      background: theme.colors.bg.secondary,
      color: theme.colors.text.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      fontSize: 12,
      fontWeight: 700
    },
    chat: {
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 24,
      overflow: 'hidden',
      minHeight: 'calc(100vh - 150px)',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: theme.isDark
        ? '0 24px 70px rgba(0, 0, 0, 0.35)'
        : '0 24px 70px rgba(15, 23, 42, 0.08)'
    },
    messages: {
      flex: 1,
      padding: hasConversation ? 22 : '46px 22px 28px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 14
    },
    message: (role) => ({
      alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
      maxWidth: '82%',
      padding: '13px 15px',
      borderRadius: role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
      background: role === 'user' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : theme.colors.bg.tertiary,
      color: role === 'user' ? 'white' : theme.colors.text.primary,
      border: role === 'user' ? 'none' : `1px solid ${theme.colors.border.primary}`,
      whiteSpace: 'pre-wrap',
      lineHeight: 1.55,
      fontSize: 14
    }),
    welcome: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: 720,
      margin: '0 auto',
      textAlign: 'center',
      width: '100%'
    },
    welcomeAvatar: {
      width: 58,
      height: 58,
      borderRadius: 16,
      display: 'grid',
      placeItems: 'center',
      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
      color: 'white',
      fontWeight: 900,
      fontSize: 22,
      boxShadow: '0 18px 35px rgba(99, 102, 241, 0.32)',
      marginBottom: 20
    },
    starterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 12,
      width: '100%',
      marginTop: 22
    },
    starterCard: {
      textAlign: 'left',
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 14,
      padding: 16,
      minHeight: 92,
      background: theme.isDark ? 'rgba(255, 255, 255, 0.035)' : '#f8f7ff',
      color: theme.colors.text.primary,
      cursor: loading ? 'not-allowed' : 'pointer'
    },
    shortcutBar: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      marginTop: 16,
      paddingTop: 16,
      borderTop: `1px solid ${theme.colors.border.primary}`,
      color: theme.colors.text.secondary,
      fontSize: 12
    },
    shortcut: {
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 8,
      padding: '4px 8px',
      background: theme.colors.bg.secondary,
      color: '#7c3aed',
      fontWeight: 800,
      cursor: loading ? 'not-allowed' : 'pointer'
    },
    composer: {
      borderTop: `1px solid ${theme.colors.border.primary}`,
      padding: 16,
      display: 'flex',
      gap: 10,
      background: theme.colors.bg.secondary
    },
    input: {
      flex: 1,
      minHeight: 46,
      padding: '12px 14px',
      borderRadius: 12,
      border: `1px solid ${theme.colors.border.primary}`,
      background: theme.colors.bg.primary,
      color: theme.colors.text.primary,
      resize: 'vertical',
      fontSize: 14,
      outline: 'none'
    },
    button: {
      border: 'none',
      borderRadius: 12,
      padding: '0 18px',
      background: loading ? 'rgba(99, 102, 241, 0.45)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
      color: 'white',
      fontWeight: 700,
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    },
    select: {
      padding: '7px 10px',
      borderRadius: 999,
      border: `1px solid ${theme.colors.border.primary}`,
      background: theme.colors.bg.secondary,
      color: theme.colors.text.secondary,
      fontSize: 12,
      fontWeight: 700,
      outline: 'none'
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={styles.avatar}>S</div>
          <h1 style={styles.title}>
            SEAn
          </h1>
          <p style={{ color: theme.colors.text.secondary, margin: 0, fontSize: 13 }}>
            AI Google Ads colleague · {accountName}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={styles.select} aria-label="Date range">
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <span style={styles.badge}><Shield size={14} /> Admin only</span>
        </div>
      </div>

      <div style={styles.chat}>
        <div style={styles.messages}>
          {!hasConversation && !loading && (
            <div style={styles.welcome}>
              <div style={styles.welcomeAvatar}>S</div>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>What would you like to look at?</h2>
              <p style={{ color: theme.colors.text.secondary, margin: '10px 0 0', maxWidth: 460, lineHeight: 1.5 }}>
                Ask SEAn a question like you would ask a colleague. New to this account? Start with one of these examples.
              </p>

              <div style={styles.starterGrid}>
                {starterCards.map((card) => (
                  <button key={card.question} onClick={() => sendMessage(card.question)} disabled={loading} style={styles.starterCard}>
                    <div style={{ color: '#8b5cf6', fontSize: 11, fontWeight: 900, letterSpacing: 1, marginBottom: 8 }}>
                      {card.label}
                    </div>
                    <div style={{ fontWeight: 800, lineHeight: 1.35 }}>{card.question}</div>
                  </button>
                ))}
              </div>

              <div style={styles.shortcutBar}>
                <span>For specialists, use</span>
                {slashShortcuts.map((item, index) => (
                  <React.Fragment key={item.command}>
                    <button onClick={() => sendMessage(item.command)} disabled={loading} style={styles.shortcut}>
                      {item.command}
                    </button>
                    {index < slashShortcuts.length - 1 && <span>or</span>}
                  </React.Fragment>
                ))}
                <span>to open a focused analysis.</span>
              </div>
            </div>
          )}

          {messages.map((message, index) => (
            <div key={index} style={styles.message(message.role)}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5, opacity: 0.8, fontSize: 12, fontWeight: 700 }}>
                {message.role === 'user' ? <User size={13} /> : <Bot size={13} />}
                {message.role === 'user' ? 'You' : 'SEAn'}
              </div>
              {message.content}
              {message.meta && (
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7 }}>
                  Context: {message.meta.period}, {message.meta.campaignCount} campaigns, {message.meta.searchTermCount} search terms, {message.meta.keywordCount} keywords
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div style={styles.message('assistant')}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} />
              SEAn is analyzing the selected account...
            </div>
          )}
        </div>

        {error && (
          <div style={{ margin: '0 16px 12px', color: '#f87171', fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={styles.composer}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask SEAn a question, or use /campaign, /searchterm, /compare..."
            style={styles.input}
          />
          <button onClick={() => sendMessage()} disabled={loading} style={styles.button}>
            {loading ? <RefreshCw size={16} /> : <Send size={16} />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
